import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import Collapse from "react-bootstrap/Collapse";
import ReactPaginate from "react-paginate";
import GoogleMapLink from "~/components/shared/google_map_link";
import InfoTooltipIcon from "~/components/shared/InfoTooltipIcon";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { formatUSDate } from "~/helpers/formatHelper";
import LeadHelpers from "~/helpers/lead_helpers";
import APIRequest from "~/lib/api_request";
import AgentAlertModal from "./AgentAlertModal/AgentAlertModal";
import AgentAlertPopover from "./AgentAlertPopover";

const agentAlertsTooltip = `
  <div class="bk-tooltip-copy">
    <p>
      Agent Alerts flag recent MLS-driven activity for an agent. Agent Alerts
      require an active Relitix subscription. To investigate access, contact
      Relitix at support@relitix.com.
    </p>
    <ul>
      <li><strong>New listing:</strong> agent associated with a newly listed property.</li>
      <li><strong>New pending:</strong> listing moved to pending status.</li>
      <li><strong>New contract:</strong> property marked under contract.</li>
      <li><strong>New closing:</strong> newly closed transaction.</li>
      <li><strong>Office change:</strong> office or brokerage change.</li>
      <li><strong>Misc:</strong> activity outside the displayed alert types.</li>
    </ul>
  </div>
`;

const LeadDrawerAgentAlerts = ({ lead = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alertsLoaded, setAlertsLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const fetchAlerts = async (page) => {
    if (!lead || !lead.lead_agent_ids_attributes) {
      return;
    }

    try {
      setLoading(true);

      const agentData = lead.lead_agent_ids_attributes.map((agent) => ({
        agent_id: agent.agent_id,
        reso_ouid: agent.multiple_listing_service_ouid,
      }));

      const response = await APIRequest.get({
        resource: "/v1/agent_alerts",
        data: {
          page,
          agent_data: agentData,
        },
      });

      setAlerts(response.body.alerts);
      setTotalPages(response.body.pagination.total_pages);
      setCurrentPage(response.body.pagination.current_page);
      setLoading(false);
      setAlertsLoaded(true);
    } catch (err) {
      setError("Failed to load agent alerts");
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkInitialState = async () => {
      const savedState = localStorage.getItem("isAgentAlertsOpen");
      const initialIsOpen = savedState ? JSON.parse(savedState) : false;

      const isFeatureEnabled = await checkFeatureFlag(
        process.env.SHAKAPACKER_PUBLIC_ALLOW_AGENT_ALERTS_SYNC,
      );

      if (initialIsOpen && !isFeatureEnabled) {
        setIsOpen(false);
        localStorage.setItem("isAgentAlertsOpen", JSON.stringify(false));
      } else {
        setIsOpen(initialIsOpen);
      }
    };

    checkInitialState();
  }, []);

  useEffect(() => {
    if (isOpen && !alertsLoaded) {
      fetchAlerts(currentPage);
    }
  }, [isOpen]);

  const onCollapseClick = async (e, togglePopover) => {
    e.preventDefault();

    const isFeatureEnabled = await checkFeatureFlag(
      process.env.SHAKAPACKER_PUBLIC_ALLOW_AGENT_ALERTS_SYNC,
    );

    if (!isFeatureEnabled) {
      togglePopover(e);
      return;
    }

    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    localStorage.setItem("isAgentAlertsOpen", JSON.stringify(newIsOpen));
  };

  const handlePageClick = (page) => {
    const selectedPage = page.selected;
    fetchAlerts(selectedPage + 1);
  };

  const handleViewClick = (alert) => {
    setSelectedAlert(alert);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAlert(null);
  };

  return (
    <div>
      <div className="pull-right">
        <AgentAlertPopover>
          {({ togglePopover }) => (
            <button
              type="button"
              className="btn btn-secondary btn-sm mr-1"
              onClick={(e) => onCollapseClick(e, togglePopover)}
            >
              {isOpen ? (
                <FontAwesomeIcon icon={["far", "fa-minus"]} />
              ) : (
                <FontAwesomeIcon icon={["far", "fa-plus"]} />
              )}
            </button>
          )}
        </AgentAlertPopover>
      </div>

      <h4 className="mb15">
        Agent Alerts{" "}
        <InfoTooltipIcon
          ariaLabel="What are Agent Alerts?"
          text={agentAlertsTooltip}
          placement="right"
          className="text-secondary"
        />
      </h4>

      <Collapse in={isOpen} mountOnEnter unmountOnExit>
        <div className="mb-3">
          {loading && (
            <div className="text-center">
              <FontAwesomeIcon icon="far fa-spinner" pulse className="mr5" />{" "}
              Loading ...
            </div>
          )}

          {error && <div className="text-center text-danger">{error}</div>}

          {!loading && !error && alerts.length === 0 && (
            <div className="text-center text-muted">
              No Agent Alerts available.
            </div>
          )}

          {!loading &&
            !error &&
            alerts.map((alert, index) => (
              <div
                key={alert.id}
                className={classNames(
                  "d-flex justify-content-between align-items-center py-3",
                  { "border-bottom": index !== alerts.length - 1 },
                  { "border-top": index === 0 },
                )}
              >
                <div className="d-flex flex-column">
                  <div>
                    <strong>{formatUSDate(alert.alert_date)}</strong>
                  </div>
                  <div>
                    <strong>Alert Type:</strong> {alert.alert_type || "N/A"}
                  </div>
                  <div>
                    <strong>Role:</strong> {alert.role || "N/A"}
                  </div>
                  <div>
                    <strong>Address:</strong>{" "}
                    {alert.unparsed_address ? (
                      <>
                        <span className="mr5">{alert.unparsed_address}</span>
                        {LeadHelpers.shouldShowGoogleMapLink(alert) && (
                          <GoogleMapLink
                            q={LeadHelpers.agentAlertFullAddress(alert)}
                          >
                            <FontAwesomeIcon
                              icon={["far", "fa-external-link-alt"]}
                            />
                          </GoogleMapLink>
                        )}
                      </>
                    ) : (
                      "N/A"
                    )}
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleViewClick(alert)}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}

          {!loading && !error && totalPages > 1 && (
            <ReactPaginate
              forcePage={currentPage - 1}
              pageCount={totalPages}
              pageRangeDisplayed={1}
              marginPagesDisplayed={2}
              containerClassName="pagination justify-content-center flex-row flex-md-column flex-lg-row"
              activeClassName="active"
              breakLabel="..."
              breakLinkClassName="page-link disabled"
              breakClassName="page-item"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              disabledClassName="disabled"
              onPageChange={handlePageClick}
              previousLabel={Rails.isMobile ? "<" : "Previous"}
              nextLabel={Rails.isMobile ? ">" : "Next"}
            />
          )}
        </div>
      </Collapse>

      {selectedAlert && (
        <AgentAlertModal
          lead={lead}
          agentAlert={selectedAlert}
          showModal={showModal}
          handleCloseModal={handleCloseModal}
        />
      )}
    </div>
  );
};

LeadDrawerAgentAlerts.propTypes = {
  lead: PropTypes.shape({}),
};

export default LeadDrawerAgentAlerts;
